"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import Lock from "@mui/icons-material/Lock";
import { X } from "lucide-react";

import { useStyles } from "./styles";

interface ModelButtonProps {
  value: string;
  label: string;
  imageSrc: string;
  selectedModel: string;
  onClick: (key: string) => void;
  disabled?: boolean;
  locked?: boolean;
}

const ModelButton: React.FC<ModelButtonProps> = ({
  value,
  label,
  imageSrc,
  selectedModel,
  onClick,
  disabled = false,
  locked = false,
}) => {
  const { classes, cx } = useStyles();
  const router = useRouter();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showTooltip && containerRef.current && tooltipRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      const top = rect.top - tooltipRect.height - 12;
      const left = rect.left + rect.width / 2 - tooltipRect.width / 2;

      setTooltipPosition({
        top: Math.max(10, top),
        left: Math.max(
          10,
          Math.min(left, window.innerWidth - tooltipRect.width - 10)
        ),
      });
    }
  }, [showTooltip]);

  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showTooltip]);

  const handleClick = () => {
    if (!locked && !disabled) {
      onClick(value);
    }
  };

  const handleLockedClick = () => {
    setShowTooltip(true);
  };

  const handleUpgrade = () => {
    router.push("/subscribe");
    setShowTooltip(false);
  };

  return (
    <div className={classes.container} ref={containerRef}>
      <button
        className={cx(
          classes.button,
          selectedModel === value && classes.selected
        )}
        disabled={disabled || locked}
        onClick={handleClick}
      >
        <img src={imageSrc} alt={`${label} Model`} className={classes.image} />
        {label}
      </button>
      {locked && (
        <>
          <div className={classes.locked}>
            <Lock />
          </div>
          <div className={classes.clickOverlay} onClick={handleLockedClick} />
        </>
      )}
      {showTooltip && (
        <div
          ref={tooltipRef}
          className={classes.tooltip}
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          <button
            className={classes.tooltipClose}
            onClick={() => setShowTooltip(false)}
          >
            <X size={14} />
          </button>
          <p className={classes.tooltipMessage}>To unlock {label}</p>
          <button className={classes.tooltipUpgrade} onClick={handleUpgrade}>
            Upgrade
          </button>
        </div>
      )}
    </div>
  );
};

export default ModelButton;
